import { oc } from "@orpc/contract";
import { z } from "zod";

const TestSuiteRunSummarySchema = z.object({
  totalCases: z.number(),
  activeCases: z.number(),
  skipped: z.number(),
  passed: z.number(),
  failed: z.number(),
  pendingReview: z.number(),
  accuracy: z.number(),
  regressions: z.number(),
  improvements: z.number(),
  outcomeCounts: z.record(z.string(), z.number()).optional(),
});

const TestSuiteCaseSchema = z.object({
  id: z.uuid(),
  testSuiteId: z.uuid(),
  testSuiteVersionId: z.uuid().nullable().optional(),
  inputData: z.record(z.string(), z.unknown()),
  expectedOutput: z.record(z.string(), z.unknown()),
  sourceStepRunId: z.uuid().nullable().optional(),
  isActive: z.boolean(),
  notes: z.string().nullable().optional(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteCaseResponse = z.infer<typeof TestSuiteCaseSchema>;

const TestSuiteSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  workflowDefinitionId: z.uuid(),
  workflowStepId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  pinnedVersionId: z.uuid().nullable().optional(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteResponse = z.infer<typeof TestSuiteSchema>;

const TestSuiteVersionSchema = z.object({
  id: z.uuid(),
  testSuiteId: z.uuid(),
  parentVersionId: z.uuid().nullable().optional(),
  status: z.enum(["draft", "finalized"]),
  version: z.number(),
  changelog: z.string().nullable().optional(),
  isPinned: z.boolean(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteVersionResponse = z.infer<typeof TestSuiteVersionSchema>;

const TestSuiteWithCasesSchema = TestSuiteSchema.extend({
  cases: z.array(TestSuiteCaseSchema),
  // Workflow version a default run will score (the workflow's pinned version).
  pinnedWorkflowVersion: z.number().nullable().optional(),
});

const TestCaseFieldDiffSchema = z.object({
  expected: z.unknown(),
  actual: z.unknown(),
  kind: z.enum(["missing", "unexpected", "mismatch"]),
});

const TestCaseDiffSchema = z.record(z.string(), TestCaseFieldDiffSchema);

export type TestCaseDiffResponse = z.infer<typeof TestCaseDiffSchema>;

const TestCaseResultSchema = z.object({
  id: z.uuid(),
  testSuiteRunId: z.uuid(),
  testCaseId: z.uuid(),
  actualOutput: z.record(z.string(), z.unknown()).nullable().optional(),
  wasSkipped: z.boolean(),
  autoScore: z.enum(["pass", "fail", "unscorable"]).nullable().optional(),
  humanVerdict: z.enum(["pass", "fail", "skip"]).nullable().optional(),
  executionTimeMs: z.number().nullable().optional(),
  error: z.string().nullable().optional(),
  diff: TestCaseDiffSchema.nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestCaseResultResponse = z.infer<typeof TestCaseResultSchema>;

const TestSuiteRunSchema = z.object({
  id: z.uuid(),
  testSuiteId: z.uuid(),
  testSuiteVersionId: z.uuid().nullable().optional(),
  workflowDefinitionId: z.uuid().nullable().optional(),
  workflowVersion: z.number().nullable().optional(),
  stepConfigSnapshot: z.record(z.string(), z.unknown()),
  configOverrides: z.record(z.string(), z.unknown()).nullable().optional(),
  configVariantLabel: z.string().nullable().optional(),
  status: z.enum(["queued", "running", "completed", "failed"]),
  summary: TestSuiteRunSummarySchema.nullable().optional(),
  previousRunId: z.uuid().nullable().optional(),
  triggeredBy: z.uuid(),
  startedAt: z.iso.datetime().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type TestSuiteRunResponse = z.infer<typeof TestSuiteRunSchema>;

const TestSuiteRunWithResultsSchema = TestSuiteRunSchema.extend({
  totalCases: z.number(),
  caseResults: z.array(
    TestCaseResultSchema.extend({
      testCase: TestSuiteCaseSchema.optional(),
      regressionStatus: z
        .enum(["regression", "improvement", "unchanged", "new"])
        .optional(),
    }),
  ),
});

const TestSuiteTrendPointSchema = z.object({
  runId: z.uuid(),
  testSuiteVersionId: z.uuid().nullable().optional(),
  suiteVersion: z.number().nullable().optional(),
  workflowDefinitionId: z.uuid().nullable().optional(),
  workflowVersion: z.number().nullable().optional(),
  accuracy: z.number().nullable().optional(),
  passed: z.number(),
  failed: z.number(),
  regressions: z.number(),
  improvements: z.number(),
  configVariantLabel: z.string().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
});

export type TestSuiteTrendPointResponse = z.infer<
  typeof TestSuiteTrendPointSchema
>;

const ProposedTestCaseSchema = z.object({
  inputData: z.record(z.string(), z.unknown()),
  proposedExpectedOutput: z.record(z.string(), z.unknown()).optional(),
  sourceStepRunId: z.uuid().optional(),
  sourceWorkflowRunId: z.uuid().optional(),
  source: z.enum(["step_run", "workflow_emails"]),
  label: z.string().optional(),
  stepRunCreatedAt: z.iso.datetime(),
});

export const testSuiteContracts = {
  listTestSuites: oc
    .input(z.object({ workflowStepId: z.uuid() }))
    .output(z.object({ testSuites: z.array(TestSuiteSchema) }))
    .route({
      summary: "List test suites for a workflow step",
      description:
        "Returns the regression test suites defined for one workflow step.",
      method: "GET",
    }),

  getTestSuite: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ testSuite: TestSuiteWithCasesSchema }))
    .route({
      summary: "Get a test suite with its cases",
      description:
        "Returns one workflow-step test suite including its test cases with input data, expected output and notes.",
      method: "GET",
    }),

  createTestSuite: oc
    .input(
      z.object({
        workflowDefinitionId: z.uuid(),
        workflowStepId: z.uuid(),
        name: z.string().min(1).max(255),
        description: z.string().optional(),
      }),
    )
    .output(z.object({ testSuite: TestSuiteSchema }))
    .route({
      summary: "Create a test suite for a workflow step",
      description:
        "Creates a named regression test suite for a step in a workflow version, with an optional description.",
      method: "POST",
    }),

  updateTestSuite: oc
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ testSuite: TestSuiteSchema }))
    .route({
      summary: "Rename or describe a test suite",
      description: "Updates a workflow-step test suite's name or description.",
      method: "PUT",
    }),

  deleteTestSuite: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a test suite",
      description: "Deletes a workflow-step test suite by ID.",
      method: "DELETE",
    }),

  addTestCases: oc
    .input(
      z.object({
        testSuiteId: z.uuid(),
        cases: z.array(
          z.object({
            inputData: z.record(z.string(), z.unknown()),
            expectedOutput: z.record(z.string(), z.unknown()),
            sourceStepRunId: z.uuid().optional(),
            notes: z.string().optional(),
          }),
        ),
      }),
    )
    .output(z.object({ cases: z.array(TestSuiteCaseSchema) }))
    .route({
      summary: "Add test cases to a test suite",
      description:
        "Adds cases to a workflow-step test suite, each with input data, expected output, optional source step run and notes.",
      method: "POST",
    }),

  updateTestCase: oc
    .input(
      z.object({
        id: z.uuid(),
        inputData: z.record(z.string(), z.unknown()).optional(),
        expectedOutput: z.record(z.string(), z.unknown()).optional(),
        isActive: z.boolean().optional(),
        notes: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ testCase: TestSuiteCaseSchema }))
    .route({
      summary: "Edit a test suite case",
      description:
        "Updates a test case's input data, expected output, notes, or active flag.",
      method: "PUT",
    }),

  deleteTestCase: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a test suite case",
      description: "Removes one case from a workflow-step test suite.",
      method: "DELETE",
    }),

  generateTestCasesFromHistory: oc
    .input(
      z.object({
        workflowStepId: z.uuid(),
        limit: z.number().min(1).max(100).optional(),
        // Optional dotted JSON path against StepRun.inputData with an
        // equality check. Used by step types whose input encodes a
        // user-meaningful selector — e.g. extract_document_content_v2
        // filters historical runs by `extractionConfig.schemaDefinitionId`
        // so the seed set only contains cases that used the schema being
        // tested.
        inputFilter: z
          .object({
            jsonPath: z.string().min(1),
            equals: z.union([z.string(), z.number(), z.boolean()]),
          })
          .optional(),
      }),
    )
    .output(z.object({ proposedCases: z.array(ProposedTestCaseSchema) }))
    .route({
      summary: "Propose test cases from past step runs",
      description:
        "Builds up to 100 proposed test cases from a workflow step's historical runs, optionally filtered by an input field value; nothing is saved.",
      method: "POST",
    }),

  runTestSuite: oc
    .input(
      z.object({
        testSuiteId: z.uuid(),
        configOverrides: z.record(z.string(), z.unknown()).optional(),
        configVariantLabel: z.string().max(255).optional(),
        // Workflow version to score. Defaults to the suite's origin
        // definition; pass a sibling version to trend the same cases across
        // workflow versions.
        workflowDefinitionId: z.uuid().optional(),
      }),
    )
    .output(z.object({ testSuiteRun: TestSuiteRunSchema }))
    .route({
      summary: "Run a test suite",
      description:
        "Starts a background run of a test suite's cases against a workflow version, with optional config overrides and variant label; returns the pending run.",
      method: "POST",
    }),

  listTestSuiteVersions: oc
    .input(z.object({ testSuiteId: z.uuid() }))
    .output(z.object({ versions: z.array(TestSuiteVersionSchema) }))
    .route({
      summary: "List versions of a test suite",
      description:
        "Returns a test suite's versions (drafts and finalized) with changelogs and which version is pinned.",
      method: "GET",
    }),

  forkTestSuiteVersion: oc
    .input(
      z.object({
        testSuiteId: z.uuid(),
        sourceVersionId: z.uuid().optional(),
      }),
    )
    .output(z.object({ version: TestSuiteVersionSchema }))
    .route({
      summary: "Fork a draft test suite version",
      description:
        "Creates a new draft version of a test suite by cloning the cases of the pinned version or a chosen finalized version.",
      method: "POST",
    }),

  finalizeTestSuiteVersion: oc
    .input(
      z.object({
        versionId: z.uuid(),
        // Optional free-text note describing what changed in this version.
        changelog: z.string().max(10000).optional(),
      }),
    )
    .output(z.object({ version: TestSuiteVersionSchema }))
    .route({
      summary: "Finalize a test suite version",
      description:
        "Freezes a draft test suite version so its cases can no longer change, with an optional changelog note.",
      method: "POST",
    }),

  publishTestSuiteVersion: oc
    .input(z.object({ testSuiteId: z.uuid(), versionId: z.uuid() }))
    .output(z.object({ testSuite: TestSuiteSchema }))
    .route({
      summary: "Publish a test suite version",
      description:
        "Pins a finalized version as the test suite's active version used for runs.",
      method: "POST",
    }),

  updateTestSuiteVersionChangelog: oc
    .input(z.object({ versionId: z.uuid(), changelog: z.string().max(10000) }))
    .output(z.object({ version: TestSuiteVersionSchema }))
    .route({
      summary: "Edit a test suite version changelog",
      description:
        "Overwrites the changelog note of a test suite version; the only field editable after finalizing.",
      method: "PUT",
    }),

  getTestSuiteTrends: oc
    .input(z.object({ testSuiteId: z.uuid() }))
    .output(z.object({ points: z.array(TestSuiteTrendPointSchema) }))
    .route({
      summary: "Get test suite pass-rate trends",
      description:
        "Returns per-run accuracy, pass and fail counts, regressions and improvements over time for a test suite, across suite versions, workflow versions and config variants.",
      method: "GET",
    }),

  getTestSuiteRun: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ testSuiteRun: TestSuiteRunWithResultsSchema }))
    .route({
      summary: "Get a test suite run with results",
      description:
        "Returns one test suite run with per-case results, scores, verdicts and regression or improvement status versus the previous run.",
      method: "GET",
    }),

  listTestSuiteRuns: oc
    .input(z.object({ testSuiteId: z.uuid() }))
    .output(z.object({ runs: z.array(TestSuiteRunSchema) }))
    .route({
      summary: "List runs of a test suite",
      description:
        "Returns past runs of a test suite with status, pass counts and config variant.",
      method: "GET",
    }),

  submitHumanVerdict: oc
    .input(
      z.object({
        testCaseResultId: z.uuid(),
        verdict: z.enum(["pass", "fail", "skip"]),
      }),
    )
    .output(z.object({ testCaseResult: TestCaseResultSchema }))
    .route({
      summary: "Record a human verdict on a test result",
      description:
        "Sets a reviewer's pass, fail or skip verdict on one test case result.",
      method: "POST",
    }),
  suggestConfigImprovements: oc
    .input(z.object({ testSuiteRunId: z.uuid() }))
    .output(
      z.object({
        suggestions: z.array(
          z.object({
            title: z.string(),
            description: z.string(),
            suggestedOverrides: z.record(z.string(), z.unknown()),
          }),
        ),
      }),
    )
    .route({
      summary: "Suggest step config fixes from a test run",
      description:
        "Uses AI to analyze a test suite run's failures and propose step configuration overrides, each with a title and explanation; nothing is applied.",
      method: "POST",
    }),
} as const;
